import { NoteType, PostNoteAdd } from "@/api/dtos";
import { FC, useCallback, useContext, useEffect, useMemo } from "react";
import InputOnlyField from "../../fields/inputOnly/InputOnlyField";
import Select from "../../inputs/Select";
import Text from "../../inputs/Text";
import noteTypeToText from "@/utilities/configuration/noteTypeToText";
import { AppSettings } from "@/AppSettings";
import Form from "../../Form";
import { Box, Button, Paper, styled } from "@mui/material";
import FormSubmitButton from "../../FormSubmitButton";
import { useLocation, useNavigate } from "react-router-dom";
import { FormError } from "@/errors";
import { IFormSubmit, IFormSubmitFailed, IFormSubmitValidationFailed, IFormValidate, useScopedField, ValidateOn } from "@ngt/forms-core";
import FormErrorType from "@/types/form/FormErrorType";
import { useSnackbar } from "notistack";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import AuthenticatedUserContext from "@/contexts/authentication/AuthenticatedUserContext";

const FormContainer = styled(Paper)(({ theme }) => ({ 
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
}));


const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'

}));

const adminTypeOptions = [
    AppSettings.noteTypes[NoteType.Annotation] ? 
        {
            id: NoteType.Annotation,
            name: noteTypeToText(NoteType.Annotation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.InternalQuery] ? 
        {
            id: NoteType.InternalQuery,
            name: noteTypeToText(NoteType.InternalQuery)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ProtocolDeviation] ? 
        {
            id: NoteType.ProtocolDeviation,
            name: noteTypeToText(NoteType.ProtocolDeviation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ImportantProtocolDeviation] ? 
        {
            id: NoteType.ImportantProtocolDeviation,
            name: noteTypeToText(NoteType.ImportantProtocolDeviation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ReasonForChange] ? 
        {
            id: NoteType.ReasonForChange,
            name: noteTypeToText(NoteType.ReasonForChange)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.SiteQuery] ? 
        {
            id: NoteType.SiteQuery,
            name: noteTypeToText(NoteType.SiteQuery)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.System] ? 
        {
            id: NoteType.System,
            name: noteTypeToText(NoteType.System)
        } :
        undefined,
].filter(x => !!x) as Array<{ id: NoteType; name: string; }>

const typeOptions = [
    AppSettings.noteTypes[NoteType.SiteQuery] ? 
        {
            id: NoteType.SiteQuery,
            name: noteTypeToText(NoteType.SiteQuery)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.Annotation] ? 
        {
            id: NoteType.Annotation,
            name: noteTypeToText(NoteType.Annotation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ProtocolDeviation] ? 
        {
            id: NoteType.ProtocolDeviation,
            name: noteTypeToText(NoteType.ProtocolDeviation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ImportantProtocolDeviation] ? 
        {
            id: NoteType.ImportantProtocolDeviation,
            name: noteTypeToText(NoteType.ImportantProtocolDeviation)
        } :
        undefined
].filter(x => !!x) as Array<{ id: NoteType; name: string; }>

export interface INoteAddFormProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
}




const NoteDescriptionSelector: FC = () => {
    const { state: { value: type } } = useScopedField<NoteType>('type', { value: true });
    const { state: { value: titleId }, context: { setValue: setTitleId} } = useScopedField('titleId', { value: true });
    const { context: { setValue: setTitle }} = useScopedField('title', { });

    useEffect(() => {
        setTitleId(undefined)
    },  [type, setTitleId])

    useEffect(() => {
        if (!type) {
            setTitle(undefined);
        }
        else if (!titleId) {
            setTitle(undefined);
        }
        else {
            setTitle(AppSettings.noteTypeTitles[type]?.find(x => x.id === titleId)?.name);
        }
    },  [titleId, type, setTitle])

    const typeTitleOptions = type ? AppSettings.noteTypeTitles[type] : undefined;

    return (
        <> 
            {
                (typeTitleOptions) ?
                    <InputOnlyField
                        propertyPath="titleId"
                        label="Description"
                        helperText="A description of the note or query."
                        FormControlProps={{
                            margin: 'normal'
                        }}
                        nullOption={false}
                        component={Select}
                        options={typeTitleOptions}
                    /> :
                    <InputOnlyField
                        propertyPath="title"
                        label="Description"
                        helperText="A description of the note or query."
                        margin="normal"
                        component={Text}
                    />  
            }
        </>
    )
}

const NoteAddForm: FC<INoteAddFormProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const client = useContext(ServiceStackClientContext);
    
    const initialData = useMemo(() => {
        return new PostNoteAdd({
            trialId,
            masterGroupId,
            coordinatingGroupId,
            institutionId,
            participantId,
            studyEventOid,
            studyEventRepeat,
            caseReportFormOid,
            caseReportFormVersionOid,
            itemGroupName,
            itemGroupRepeat,
            itemName,
            type: NoteType.SiteQuery
        })

    }, [
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName
    ]);

    const location = useLocation();
    const navigate = useNavigate();
    const user = useContext(AuthenticatedUserContext);

    const isAdmin = useMemo(() => {
        return user.data?.roles?.includes('Admin');
    }, [user])

    const returnUrl = useMemo(() => {
        return location.pathname.replace('/add', '');
    }, [location.pathname]);

    
    const validate = useCallback<IFormValidate<PostNoteAdd, FormError>>(async (formState, formActions) => {
        let errors: Record<string, FormError[]> = {};

        if (!formState?.value?.type) {
            errors['type'] = [
                {
                    property: 'type',
                    message: 'Type must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        const titleOptions = formState?.value?.type ? AppSettings.noteTypeTitles[formState?.value?.type] : undefined;

        if (titleOptions && !(formState?.value as any)?.titleId) {
            errors['titleId'] = [
                {
                    property: 'titleId',
                    message: 'Description must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (!formState?.value?.title) {
            errors['title'] = [
                {
                    property: 'title',
                    message: 'Description must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (formState?.value?.title && formState.value.title.length > 255) {
            errors['title'] = [
                {
                    property: 'title',
                    message: 'Description must not exceed 255 characters!',
                    type: FormErrorType.Normal
                }
            ];
        }

        return errors;
    }, []);

    const submit = useCallback<IFormSubmit<PostNoteAdd, FormError>>(async (formState, formActions) => {
        await client.post(new PostNoteAdd({ ...formState.value }));

        enqueueSnackbar(`Note successfully saved!`, { variant: "success" });

        navigate(returnUrl);
        
        return {};
    }, [client, enqueueSnackbar, navigate, returnUrl]);

    const submitFailed = useCallback<IFormSubmitFailed<PostNoteAdd, FormError>>(async (formState, formActions) => {
        
        enqueueSnackbar(`An error occurred while attempting to save the note!`, { variant: "error" });

        return {};
    }, [enqueueSnackbar]);

    const submitValidationFailed = useCallback<IFormSubmitValidationFailed<PostNoteAdd, FormError>>(async (formState, formActions, validationException) => {
        enqueueSnackbar("The form contains invalid data, please correct the marked fields and try again!", { variant: "warning" });

        return formState.errors;
    }, [enqueueSnackbar]);

    return (
        <Form
            initialValue={initialData}
            debug={AppSettings.debug}
            onSubmit={submit}
            onSubmitFailed={submitFailed}
            onSubmitValidationFailed={submitValidationFailed}
            onValidate={validate}
            validateOn={ValidateOn.onChange}
        >
            <FormContainer>           
                <InputOnlyField
                    propertyPath="type"
                    label="Type"
                    helperText="The type of note or query to be added."
                    FormControlProps={{
                        margin: 'normal'
                    }}
                    nullOption={false}
                    component={Select}
                    options={isAdmin ? adminTypeOptions : typeOptions}
                />    
                <NoteDescriptionSelector /> 
                <InputOnlyField
                    propertyPath="details"
                    label="Detailed Note"
                    helperText="The complete details of the note or query."
                    margin="normal"
                    component={Text}
                    multiline
                    minRows={2}
                />      
            </FormContainer>  
            <FormButtons>
                <FormSubmitButton
                    variant="contained"
                    color="primary"
                >
                    Save
                </FormSubmitButton>
                <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => navigate(returnUrl)}
                >
                    Cancel
                </Button>
            </FormButtons>    
        </Form>
    )
}

export default NoteAddForm;